import React, { useState, useRef, useEffect } from 'react';
import styles from './Chat.module.css';
import { ChatInputProps } from './types';
import { DEBUG } from 'lib/util/debug';
import { useTranslation } from 'react-i18next';

export const ChatInput = React.forwardRef<HTMLTextAreaElement, ChatInputProps>(
  ({ onSendMessage }, ref) => {
    const { t } = useTranslation();
    const [message, setMessage] = useState('');
    const [isComposing, setIsComposing] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const adjustTextareaHeight = () => {
      DEBUG.log('textarea.scrollHeight', textareaRef.current?.scrollHeight);

      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    };

    useEffect(() => {
      adjustTextareaHeight();
    }, [message]);

    useEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Adjust textarea height when resizing occurs - this is needed when the chat input's
      // parent components are initially hidden in React and then become visible again
      const resizeObserver = new ResizeObserver(() => {
        adjustTextareaHeight();

        // Set focus to textarea if it doesn't already have focus
        if (document.activeElement !== textarea) {
          textarea.focus();
        }
      });

      resizeObserver.observe(textarea);

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    useEffect(() => {
      if (!containerRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            adjustTextareaHeight();
          }
        },
        { threshold: 0 }
      );

      observer.observe(containerRef.current);

      return () => {
        observer.disconnect();
      };
    }, []);

    const handleSubmit = () => {
      if (message.trim()) {
        onSendMessage(message);
        setMessage('');
        // Reset height after sending
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
        e.preventDefault();
        handleSubmit();
      }
    };

    return (
      <div ref={containerRef} className={styles.inputContainer}>
        <div className={styles.inputBox}>
          <label htmlFor="messageInput" className={styles.visuallyHidden}>
            {t('Type a message')}
          </label>
          <textarea
            autoFocus
            ref={(el) => {
              if (!el) return;

              if (ref) {
                if (typeof ref === 'function') {
                  ref(el);
                } else {
                  (ref as React.MutableRefObject<HTMLTextAreaElement>).current = el;
                }
              }
              if (textareaRef) {
                (textareaRef as React.MutableRefObject<HTMLTextAreaElement>).current = el;
              }
            }}
            id="messageInput"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            className={styles.messageInput}
            placeholder={t('Message')}
            aria-label="Type a message"
            rows={1}
          />
          <button className={styles.sendButton} aria-label="Send message" onClick={handleSubmit}>
            <img src="/static/images/map/mail-send.svg" />
          </button>
        </div>
      </div>
    );
  }
);

ChatInput.displayName = 'ChatInput';
